import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MonthlySpendBreakdown } from "@/lib/fuel-analytics";
import { GlassDonutChart } from "@/components/charts/GlassDonutChart";
import { FuelDayByDayPanel } from "@/components/fuel-activity/FuelDayByDayPanel";
import { FuelSpendRankingPanel } from "@/components/fuel-activity/FuelSpendRankingPanel";
import {
  AXIS_TICK,
  BAR_ANIMATION,
  ChartGradientDefs,
  ChartTooltip,
  GRID_STROKE,
} from "@/components/charts/chart-kit";
import { BRAND } from "@/lib/brand-colors";
import { formatCurrency, formatFuelPrice, formatNumber } from "@/lib/utils";

type Props = {
  breakdown: MonthlySpendBreakdown;
  scope?: "platform" | "user";
};

export function MonthlySpendBreakdownView({ breakdown, scope = "platform" }: Props) {
  const { overview, label, year, month, spendByDay, spendByGrade, spendByUser, spendByCar, spendByStation, dayRows } =
    breakdown;

  return (
    <div className="space-y-5">
      <div className="stagger-children grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Total spend</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">{formatCurrency(overview.totalSpent)}</p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Fill-ups</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">{formatNumber(overview.totalFillUps)}</p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Litres</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">
            {formatNumber(Math.round(overview.totalLitres))} L
          </p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Active users</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">{formatNumber(overview.activeUsers)}</p>
          <p className="mt-1 text-xs text-muted">
            {formatFuelPrice(overview.avgPricePerLitre)}/L · {formatCurrency(overview.avgFillCost)}/fill
          </p>
        </div>
      </div>

      {breakdown.vsPriorMonthPercent != null && breakdown.priorMonthSpent != null ? (
        <p className="rounded-2xl border border-border bg-canvas px-4 py-3 text-sm text-muted">
          {breakdown.vsPriorMonthPercent >= 0 ? "Up" : "Down"}{" "}
          {Math.abs(breakdown.vsPriorMonthPercent)}% vs previous month (
          {formatCurrency(breakdown.priorMonthSpent)}).
        </p>
      ) : null}

      <div className="rounded-3xl bg-canvas p-4 sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Daily spend</p>
        <p className="mt-0.5 text-xs text-muted">Spend across each day in {label}</p>
        <div className="mt-4 h-[240px] w-full">
          {spendByDay.some((entry) => entry.value > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendByDay} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <ChartGradientDefs />
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip
                      active={active}
                      payload={payload}
                      label={label}
                      valueLabel=""
                      formatValue={(value) => formatCurrency(value)}
                    />
                  )}
                  cursor={{ fill: BRAND.accentMuted, radius: 8 }}
                />
                <Bar
                  dataKey="value"
                  radius={[10, 10, 4, 4]}
                  maxBarSize={32}
                  fill="url(#gradAccentY)"
                  {...BAR_ANIMATION}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted">No daily spend data.</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
        <div className="rounded-3xl bg-canvas p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Spend by grade</p>
          <div className="mt-4 h-[220px]">
            {spendByGrade.length > 0 ? (
              <GlassDonutChart data={spendByGrade} valueLabel="JMD" maxSlices={6} minLabelPercent={0.03} />
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-muted">No grade data.</p>
            )}
          </div>
        </div>

        <div className="min-h-[280px]">
          <FuelDayByDayPanel dayRows={dayRows} year={year} month={month} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
        {scope === "platform" ? (
          <FuelSpendRankingPanel title="Top users by spend" entries={spendByUser} kind="users" />
        ) : (
          <FuelSpendRankingPanel title="Spend by vehicle" entries={spendByCar} kind="vehicles" />
        )}

        <FuelSpendRankingPanel
          title="Top stations by spend"
          entries={spendByStation}
          kind="stations"
          aggregateByName
        />
      </div>
    </div>
  );
}
